import React, { useState, useEffect, useCallback } from 'react';
import { playersAPI } from '../services/api';
import API from '../services/api';
import { MARKET_REFRESH_MS } from '../services/api';
import PlayerCard from '../components/PlayerCard';
import Toast from '../components/Toast';
import MarketLockBanner from '../components/MarketLockBanner';
import OrderBucket from '../components/OrderBucket';
import useMarketStatus from '../hooks/useMarketStatus';
import { useAuth } from '../context/AuthContext';
import { Search } from 'lucide-react';

const ROLES    = ['All', 'batsman', 'bowler', 'all-rounder', 'wicket-keeper'];
const ROLE_LABELS = { batsman: 'BAT', bowler: 'BOWL', 'all-rounder': 'ALL', 'wicket-keeper': 'WK' };
const TEAMS    = ['All', 'CSK', 'MI', 'RCB', 'KKR', 'SRH', 'DC', 'PBKS', 'RR', 'GT', 'LSG'];
const SORTS    = [
  { value: 'price_desc',  label: 'Price ↓' },
  { value: 'price_asc',   label: 'Price ↑' },
  { value: 'change_desc', label: 'Gainers' },
  { value: 'change_asc',  label: 'Losers' },
  { value: 'name_asc',    label: 'A–Z' },
];

export default function MarketPage() {
  const { updateAfterTrade }     = useAuth();
  const [players, setPlayers]     = useState([]);
  const [portfolio, setPortfolio] = useState({});
  const [bucket, setBucket]       = useState({});
  const [bucketData, setBucketData] = useState({ orders: [], summary: null });
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [role, setRole]           = useState('All');
  const [team, setTeam]           = useState('All');
  const [sort, setSort]           = useState('price_desc');
  const [toast, setToast]         = useState(null);
  const { status: marketStatus }  = useMarketStatus();

  const fetchData = useCallback(async () => {
    try {
      const [pRes, portRes] = await Promise.all([
        playersAPI.getAll(),
        playersAPI.getPortfolio(),
      ]);
      setPlayers(pRes.data);
      const portMap = {};
      portRes.data.forEach(h => { portMap[h.player_id] = h; });
      setPortfolio(portMap);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchBucket = useCallback(async () => {
    try {
      const r = await API.get('/bucket');
      const map = {};
      (r.data.orders || []).forEach(o => { map[o.player_id] = o; });
      setBucket(map);
      setBucketData({
        orders: r.data.orders || [],
        summary: r.data.summary || null,
      });
    } catch {
      setBucket({});
      setBucketData({ orders: [], summary: null });
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchBucket();
    const dataInterval = setInterval(fetchData, MARKET_REFRESH_MS);
    const bucketInterval = setInterval(fetchBucket, MARKET_REFRESH_MS);
    return () => {
      clearInterval(dataInterval);
      clearInterval(bucketInterval);
    };
  }, [fetchData, fetchBucket]);

  const filtered = players
    .filter(p => {
      if (role !== 'All' && p.role !== role) return false;
      if (team !== 'All' && p.team_short !== team) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.team_short?.toLowerCase().includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sort === 'price_desc')  return parseFloat(b.current_stock_price) - parseFloat(a.current_stock_price);
      if (sort === 'price_asc')   return parseFloat(a.current_stock_price) - parseFloat(b.current_stock_price);
      if (sort === 'change_desc') return parseFloat(b.price_change_pct||0) - parseFloat(a.price_change_pct||0);
      if (sort === 'change_asc')  return parseFloat(a.price_change_pct||0) - parseFloat(b.price_change_pct||0);
      if (sort === 'name_asc')    return a.name.localeCompare(b.name);
      return 0;
    });

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <div className="page-header">
        <h1 className="page-title">Market</h1>
        <p className="page-subtitle">
          {players.length} players · Add to bucket, then execute together
        </p>
      </div>

      <MarketLockBanner status={marketStatus} />

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input
          className="search-input"
          style={{ paddingLeft: 36, width: '100%' }}
          placeholder="Search player or team..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
        {/* Role */}
        {ROLES.map(r => (
          <button key={r} className={`filter-chip${role === r ? ' active' : ''}`} onClick={() => setRole(r)}>
            {r === 'All' ? 'All Roles' : ROLE_LABELS[r] || r}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />
        {/* Sort */}
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '4px 10px', color: 'var(--text-secondary)', fontSize: '0.78rem', cursor: 'pointer', outline: 'none' }}
        >
          {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Team filter */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '1rem' }}>
        {TEAMS.map(t => (
          <button key={t} className={`filter-chip${team === t ? ' active' : ''}`} style={{ fontSize: '0.74rem', padding: '3px 10px' }} onClick={() => setTeam(t)}>
            {t}
          </button>
        ))}
      </div>

      {/* Player grid */}
      <div className="players-grid">
        {filtered.map(player => (
          <PlayerCard
            key={player.id}
            player={player}
            holding={portfolio[player.id]}
            bucketOrder={bucket[player.id]}
            marketStatus={marketStatus}
            onBucketChange={fetchBucket}
            onToast={(msg, type) => setToast({ msg, type })}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            No players found matching your filters.
          </div>
        )}
      </div>

      {/* Order bucket floating button */}
      <OrderBucket
        bucket={bucketData}
        refreshBucket={fetchBucket}
        onExecuted={(tradeData) => {
          if (tradeData) updateAfterTrade(tradeData);
          fetchData();
          fetchBucket();
        }}
        onToast={(msg, type) => setToast({ msg, type })}
      />
    </div>
  );
}
